import React, { useCallback } from 'react';
import {
  ArrayField,
  ArrayInput,
  Datagrid,
  Form,
  required,
  SimpleFormIterator,
  TextField,
  useNotify,
  usePermissions,
  useRecordContext,
  useUpdate,
} from 'react-admin';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CreateIcon from '@mui/icons-material/Create';
import Button from '@mui/material/Button';

import { CardContent, IconButton } from '@mui/material';
import actions, { Actions } from '../../constants/actions';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import hasAccess from '../../utilities/hasAccess';
import useDialogStatus from '../../hooks/useDialogStatus';
import { IAccountEntity } from '../../utilities/schemas/accountEntity';
import ReferenceEntityInputV2 from '../../customFields/ReferenceEntityInputV2';
import entityTypes from '../../constants/entityTypes';
import PermissionMissing from '../utils/PermissionMissing';
import useHiddenUntilHover from '../../hooks/useHiddenUntilHover';

type AccountEntitiesFormData = {
  connectedEntities?: { id: string; type: string }[];
}

const EditAccountHoldersDialog = ({ buttonId }: { buttonId: string }) => {
  const notify = useNotify();
  const record = useRecordContext<IAccountEntity>();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const [update] = useUpdate();

  const handleSave = useCallback((data: AccountEntitiesFormData) => {
    update('account-entities', { id: record?.id, data }, {
      onError: () => notify('Could not update account holders', { type: 'error' }),
      onSuccess: closeDialog,
      mutationMode: 'pessimistic',
    });
  }, [closeDialog, record?.id, notify, update]);

  return (
    <>
      <IconButton size="small" onClick={openDialog} id={buttonId}>
        <CreateIcon color="secondary" fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={closeDialog} maxWidth="lg">
        <Form
          record={{
            connectedEntities: record?.connectedEntities,
          }}
          onSubmit={handleSave}
        >
          <DialogTitle>Account holders</DialogTitle>
          <DialogContent>
            <ArrayInput source="connectedEntities" label={false} fullWidth defaultValue={[{}]}>
              <SimpleFormIterator inline sx={{ '.RaSimpleFormIterator-form': { marginY: 2 } }} disableReordering>
                <ReferenceEntityInputV2 source="" validate={[required()]} label="Entity" allowedTypes={[entityTypes.INDIVIDUAL, entityTypes.BUSINESS]} />
              </SimpleFormIterator>
            </ArrayInput>
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" variant="contained">Save</Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

const AccountHoldersList = () => {
  const { permissions } = usePermissions<Actions[]>();

  const canListAccountHolders = hasAccess(permissions, actions.INDIVIDUAL_LIST)
    && hasAccess(permissions, actions.BUSINESS_LIST);
  const canUpdateAccountHolders = hasAccess(permissions, actions.ACCOUNT_ENTITY_UPDATE);

  const { hiddenElementId, wrapperSx } = useHiddenUntilHover();

  return (
    <Card
      variant="outlined"
      sx={wrapperSx}
    >
      <CardHeader
        title="Account holders"
        action={canUpdateAccountHolders
          && canListAccountHolders
          && (<EditAccountHoldersDialog buttonId={hiddenElementId} />)}
      />
      <Divider />
      { canListAccountHolders ? (
        <ArrayField source="connectedEntities">
          <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
            <ReferenceEntityField sortable={false} source="" label="ID">
              <EntityDisplayNameField />
            </ReferenceEntityField>
            <TextField sortable={false} source="type" />
          </Datagrid>
        </ArrayField>
      ) : (
        <CardContent>
          <PermissionMissing />
        </CardContent>
      )}
    </Card>
  );
};

export default AccountHoldersList;
